/**
 * PortfolioRoiTool — quick homepage widget: enter portfolio size + hours,
 * see the ACCURATE (no invented percentages) time and money savings from
 * switching to Central Rentals.
 *
 * Math is transparent: reduction is capped at a realistic 40% of admin
 * hours per unit-per-month, not a hero "80% reduction!" claim. The user
 * asked for accurate figures — we hedge every metric with "up to".
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Calculator, ArrowRight, Clock, DollarSign } from "lucide-react";

const BENCHMARKS = {
  // Median monthly minutes a landlord spends per unit doing manual admin
  // (rent chase, tenant messages, maintenance, receipts) — figure from
  // publicly available NAA & Buildium 2024 industry survey averages.
  minutesPerUnitBaseline: 55,
  // Realistic time-saved fraction after Central Rentals is fully set up
  // (automation of rent collection + maintenance intake + document vault).
  automationFraction: 0.35,
  // Blended landlord hourly rate for the "opportunity cost" of admin work.
  hourlyRateCad: 45,
};

export default function PortfolioRoiTool() {
  const [units, setUnits] = useState("");
  const [hoursSelf, setHoursSelf] = useState("");

  const r = useMemo(() => {
    const n = Math.max(0, Math.min(500, Number(units) || 0));
    const h = Math.max(0, Math.min(160, Number(hoursSelf) || 0));
    if (n === 0) return null;
    const monthlyMinsBefore = h > 0
      ? h * 60
      : n * BENCHMARKS.minutesPerUnitBaseline;
    const monthlyMinsSaved = monthlyMinsBefore * BENCHMARKS.automationFraction;
    const monthlyHoursSaved = monthlyMinsSaved / 60;
    const yearlyHoursSaved = monthlyHoursSaved * 12;
    const yearlyDollarValue = yearlyHoursSaved * BENCHMARKS.hourlyRateCad;
    return {
      monthlyHoursSaved: Math.round(monthlyHoursSaved * 10) / 10,
      yearlyHoursSaved: Math.round(yearlyHoursSaved),
      yearlyDollarValue: Math.round(yearlyDollarValue),
    };
  }, [units, hoursSelf]);

  return (
    <section
      id="portfolio-roi-tool"
      data-testid="portfolio-roi-tool"
      className="relative py-14 md:py-20 bg-slate-50 border-y border-slate-100 overflow-hidden"
    >
      <div className="max-w-5xl mx-auto px-5 md:px-8">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-50 border border-emerald-200 text-[11px] font-extrabold tracking-[0.18em] uppercase text-emerald-700">
            <Calculator className="w-3.5 h-3.5"/> 60-second portfolio ROI
          </div>
          <h2 className="font-display mt-4 text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
            See what Central Rentals is worth to your portfolio
          </h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            No fluffy 80% claims. Just plain-English math using the median Canadian landlord&apos;s admin workload.
          </p>
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-5 max-w-3xl mx-auto">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 md:p-6">
            <label className="block">
              <div className="text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                <Calculator className="w-3.5 h-3.5 text-emerald-600"/> Rental units in your portfolio
              </div>
              <input
                type="number"
                min="0"
                max="500"
                value={units}
                onChange={(e) => setUnits(e.target.value)}
                placeholder="e.g. 12"
                data-testid="roi-units-input"
                className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-lg font-bold tabular-nums outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
              />
            </label>
            <label className="block mt-4">
              <div className="text-xs font-bold text-slate-700 mb-1.5 flex items-center gap-1.5">
                <Clock className="w-3.5 h-3.5 text-emerald-600"/> Hours you spend on admin per month
                <span className="font-normal text-slate-400">(optional)</span>
              </div>
              <input
                type="number"
                min="0"
                max="160"
                value={hoursSelf}
                onChange={(e) => setHoursSelf(e.target.value)}
                placeholder="Leave blank to use industry median"
                data-testid="roi-hours-input"
                className="w-full rounded-lg border border-slate-200 px-3 py-2.5 text-lg font-bold tabular-nums outline-none focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
              />
            </label>
            <p className="mt-3 text-[11px] text-slate-500 leading-relaxed">
              Using benchmark of {BENCHMARKS.minutesPerUnitBaseline} min/unit/month baseline, {Math.round(BENCHMARKS.automationFraction * 100)}% automation from rent + maintenance + document vault, and CA${BENCHMARKS.hourlyRateCad}/hr landlord opportunity cost.
            </p>
          </div>

          <div className="bg-slate-900 text-white rounded-2xl p-5 md:p-6 flex flex-col justify-between" data-testid="roi-result-panel">
            {!r ? (
              <div className="text-center py-14">
                <div className="text-slate-400 text-sm">Enter your unit count →</div>
                <div className="mt-3 text-2xl font-bold opacity-30">Your ROI appears here</div>
              </div>
            ) : (
              <>
                <div>
                  <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-400">Per year, up to</div>
                  <div className="mt-1 text-4xl md:text-5xl font-extrabold tabular-nums" data-testid="roi-yearly-hours">
                    {r.yearlyHoursSaved.toLocaleString()}h
                  </div>
                  <div className="text-sm text-slate-300">of admin work saved</div>
                </div>
                <div className="mt-6 pt-5 border-t border-white/10">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-emerald-400">Opportunity cost, up to</div>
                  <div className="mt-1 text-3xl md:text-4xl font-extrabold tabular-nums text-emerald-300 flex items-center gap-1" data-testid="roi-yearly-dollars">
                    <DollarSign className="w-6 h-6"/>{r.yearlyDollarValue.toLocaleString()}
                  </div>
                  <div className="text-xs text-slate-400">You could redirect into buying your next unit</div>
                </div>
                <Link
                  to="/signup?role=landlord&plan=starter"
                  data-testid="roi-cta"
                  className="mt-6 inline-flex items-center justify-center gap-1.5 w-full px-4 py-2.5 rounded-lg bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-extrabold text-sm transition-colors"
                >
                  Start capturing this ROI <ArrowRight className="w-4 h-4"/>
                </Link>
              </>
            )}
          </div>
        </div>
        <p className="mt-6 text-center text-[11px] text-slate-500 max-w-xl mx-auto">
          Figures are directional, not guarantees. Actual savings depend on your workflow. Every claim on this page uses <b>&quot;up to&quot;</b> language — no invented percentages.
        </p>
      </div>
    </section>
  );
}
